import CheckIcon from "@mui/icons-material/Check";
import { Button } from "@mui/material";

import { useToast } from "@qubit/autoparts";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { NextEmptyBinSuccessAction, fetchPortStatus } from "~/redux/actions";
import { selectPortStateByPort } from "~/redux/selectors/autostoreSelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import {
  useNextEmptyBinMutation,
  useStartInductionSinglePortMutation
} from "~/redux/warehouse/autostoreGrid.hooks";
import { useLazyGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";

import {
  selectCompartment,
  selectPreviousPutawayTask,
  setPreviousBinNumber
} from "./autostorePutaway.slice";

/**
 * This button shares the same text and appearance as the ConfirmInductionButton.
 * It is used to exit the previous bin flow and resume normal induction.
 */
export function ExitPreviousBinButton() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { errorToast } = useToast();
  const trackedPageName = useMixpanelPageName();
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const portStateByPort = useAppSelector(selectPortStateByPort);
  const previousPutawayTask = useAppSelector(selectPreviousPutawayTask);

  const [
    startInductionSinglePort,
    { isUninitialized: isStartInductionUninitialized }
  ] = useStartInductionSinglePortMutation();
  const [getWorkstations] = useLazyGetWorkstationsQuery();
  const [getNextEmptyBin] = useNextEmptyBinMutation();

  const previousPortState =
    previousPutawayTask && portStateByPort[previousPutawayTask.portId];

  const disabled =
    (previousPortState && !previousPortState.getPortResponse.isReady) ||
    !isStartInductionUninitialized;

  /**
   * We exit previous bin by:
   * 1. Calling start-induction
   * 2. Calling nextEmptyBin for each port
   * 3. Fetching the port status to start port polling
   */
  const handleExitPreviousBinFlow = async () => {
    if (!siteWorkstation || !previousPutawayTask) {
      return;
    }
    mixpanelTrack({
      trackedPageName,
      type: "Button Click",
      label: "Confirm Induction",
      eventProperties: {
        BinId: previousPutawayTask?.binId,
        Compartment: previousPutawayTask?.compartment,
        PortId: previousPutawayTask?.portId
      }
    });
    try {
      await startInductionSinglePort({
        autostoreGridId: siteWorkstation.autostoreGridId,
        portId: previousPutawayTask.portId
      }).unwrap();

      await getWorkstations().unwrap();
      dispatch(selectCompartment(undefined));

      const nextEmptyBin = await getNextEmptyBin({
        autostoreGridId: siteWorkstation.autostoreGridId,
        portId: previousPutawayTask.portId
      }).unwrap();
      dispatch<NextEmptyBinSuccessAction>({
        type: "autostore/NEXT_EMPTY_BIN_SUCCESS",
        payload: {
          nextEmptyBin,
          portId: previousPutawayTask.portId
        }
      });

      for (const port of siteWorkstation.ports) {
        await dispatch(fetchPortStatus({ portId: port.portId }));
      }
    } catch (error) {
      errorToast("Resume induction failed", {
        description: getMessageFromRtkError(error)
      });
    }

    dispatch(setPreviousBinNumber(null));
  };

  return (
    <Button
      style={{ width: "100%" }}
      size="large"
      startIcon={<CheckIcon />}
      onClick={handleExitPreviousBinFlow}
      disabled={disabled}
    >
      {t("confirm induction")}
    </Button>
  );
}
